import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useMessages } from '../../components/Messages';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { showApiError } from '../../utils/errorUtils';
import './Users.css';
import { formatAppDate } from '../../utils/dateUtils';

const ViewCountry = forwardRef(({ countryId, onBack, editMode = false, createMode = false, onSave, onCancel, onEdit, onCountryCreated }, ref) => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const [country, setCountry] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const { showSuccess, showError, showWarning } = useMessages();

  useImperativeHandle(ref, () => ({
    handleDelete,
    handleSave
  }));

  useEffect(() => {
    if (countryId && !createMode) {
      fetchCountry();
    } else if (createMode) {
      // Initialize with default values for new country
      const newCountry = {
        country_code: '',
        country_name: '',
        currency_code: 'USD',
        is_active: true
      };
      setCountry(newCountry);
      setEditData(newCountry);
    }
  }, [countryId, createMode]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (editMode || createMode) {
          handleCancel();
        } else {
          // In view mode, go back to list
          if (onBack) onBack();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editMode, createMode, onBack]);

  const fetchCountry = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/countries/${countryId}`));
      if (response.ok) {
        const data = await response.json();
        setCountry(data);
        setEditData(data);
      } else {
        setError(t('countries.view.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching country:', error);
      setError(t('countries.view.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      // Validate required fields
      if (!editData.country_code || !editData.country_name) {
        showError(t('countries.view.validation.required'));
        return;
      }

      // Prepare data for API
      const saveData = {
        country_code: editData.country_code,
        country_name: editData.country_name,
        currency_code: editData.currency_code || null,
        is_active: editData.is_active
      };

      const url = createMode
        ? getApiUrl('/api/countries')
        : getApiUrl(`/api/countries/${countryId}`);

      const method = createMode ? 'POST' : 'PUT';

      const response = await apiFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(saveData)
      });

      if (response.ok) {
        const updatedCountry = await response.json();
        setCountry(updatedCountry);
        setEditData(updatedCountry);
        setHasChanges(false);

        if (createMode) {
          showSuccess(t('countries.view.messages.createdSuccess'));
          if (onCountryCreated) {
            onCountryCreated(updatedCountry.country_id);
          }
        } else {
          showSuccess(t('countries.view.messages.updatedSuccess'));
        }

        // Call onSave callback if provided
        if (onSave) {
          onSave();
        }
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error saving country:', error);
      showError(tCommon('errors.genericTitle'), t('countries.view.error.loadingError'));
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm(t('countries.view.confirmations.unsavedChanges'))) {
        setEditData(country || {});
        setHasChanges(false);
        if (onCancel) onCancel();
      }
    } else {
      if (onCancel) onCancel();
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('countries.view.confirmations.deleteConfirm', { name: country.country_name }))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/countries/${countryId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        showSuccess(t('countries.view.messages.deletedSuccess'));
        if (onBack) onBack();
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error deleting country:', error);
      showError(tCommon('errors.genericTitle'), t('countries.view.error.fetchFailed'));
    }
  };

  if (loading) {
    return <div className="view-container"><div className="loading">{t('countries.loading')}</div></div>;
  }

  if (error && !country) {
    const errorMessage = typeof error === 'string' ? error : 'An error occurred while loading the country';
    return <div className="view-container"><div className="error-message">{errorMessage}</div></div>;
  }

  if (!country && !createMode) {
    return <div className="view-container"><div className="no-data">{t('countries.view.titles.noCountryFound')}</div></div>;
  }

  return (
    <div className="view-container">
      <div className="view-header">
        <div className="header-title">
          <h2>
            {createMode ? t('countries.view.titles.newCountry') : country?.country_name || t('countries.view.titles.viewCountry')}
          </h2>
          <span className="header-id">{!createMode && country?.country_code}</span>
        </div>
        <div className="header-status">
          {editData.is_active ? (
            <span className="status-badge status-active">{t('countries.filters.status.active')}</span>
          ) : (
            <span className="status-badge status-inactive">{t('countries.filters.status.inactive')}</span>
          )}
        </div>
      </div>

      <div className="view-content">
        <div className="form-section">
          <h3>{t('countries.view.sections.countryInfo')}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>{t('countries.view.fields.code')} *</label>
              <input
                type="text"
                value={editData.country_code || ''}
                onChange={(e) => handleFieldChange('country_code', e.target.value.toUpperCase())}
                disabled={!editMode && !createMode}
                required
                maxLength="3"
              />
            </div>
            <div className="form-group">
              <label>{t('countries.view.fields.name')} *</label>
              <input
                type="text"
                value={editData.country_name || ''}
                onChange={(e) => handleFieldChange('country_name', e.target.value)}
                disabled={!editMode && !createMode}
                required
              />
            </div>
            <div className="form-group">
              <label>{t('countries.view.fields.currency')}</label>
              <input
                type="text"
                value={editData.currency_code || ''}
                onChange={(e) => handleFieldChange('currency_code', e.target.value.toUpperCase())}
                disabled={!editMode && !createMode}
                maxLength="3"
              />
            </div>
            <div className="form-group">
              <label>{t('countries.view.fields.status')}</label>
              <select
                value={editData.is_active ? 'active' : 'inactive'}
                onChange={(e) => handleFieldChange('is_active', e.target.value === 'active')}
                disabled={!editMode && !createMode}
              >
                <option value="active">{t('countries.filters.status.active')}</option>
                <option value="inactive">{t('countries.filters.status.inactive')}</option>
              </select>
            </div>
          </div>
        </div>

        {country && country.created_date && (
          <div className="form-section">
            <h3>{t('countries.view.sections.systemInfo')}</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>{t('countries.view.fields.createdDate')}</label>
                <span>{formatAppDate(country.created_date, 'datetime')}</span>
              </div>
              {country.updated_date && (
                <div className="info-item">
                  <label>{t('countries.view.fields.lastUpdated')}</label>
                  <span>{formatAppDate(country.updated_date, 'datetime')}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default ViewCountry;
